import { Link } from "react-router-dom";
import { Anchor, Ship, MapPin, Phone, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { WaveDivider, WaveDividerAlt } from "@/components/WaveDivider";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_seaside-bites-6/artifacts/ekmsrivy_banner%20final%20SSB.png";
const PIRATE_PHOTO_URL = "https://customer-assets.emergentagent.com/job_c88a251c-afe1-4112-b2ce-9b11c23936f4/artifacts/sxav2hb5_website%20photo.jpg";

const HeroSection = () => (
  <section
    data-testid="hero-section"
    className="relative min-h-screen flex items-center justify-center"
    style={{
      background: "linear-gradient(135deg, #E91E8C 0%, #C8147A 30%, #00B4D8 100%)",
    }}
  >
    <div className="absolute inset-0 overflow-hidden">
      <div className="absolute top-20 left-10 opacity-10">
        <Anchor className="w-32 h-32 text-white" />
      </div>
      <div className="absolute bottom-40 right-10 opacity-10">
        <Ship className="w-40 h-40 text-white" />
      </div>
    </div>

    <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-24 pb-16">
      <div className="mb-8 fade-in-up">
        <img
          src={LOGO_URL}
          alt="Seaside Bites On The Water - Pasco, Florida"
          data-testid="hero-logo"
          className="max-w-md md:max-w-lg lg:max-w-xl mx-auto drop-shadow-2xl"
        />
      </div>

      <p
        className="font-body text-xl md:text-2xl text-white/90 mb-10 max-w-2xl mx-auto fade-in-up"
        style={{ animationDelay: "0.2s" }}
      >
        A pink pirate-inspired food boat bringing fun, flavor, and a little
        island magic to the waters of Pasco County
      </p>

      <div
        className="flex flex-col sm:flex-row gap-4 justify-center fade-in-up"
        style={{ animationDelay: "0.4s" }}
      >
        <Link to="/menu">
          <Button
            data-testid="hero-view-menu-btn"
            className="btn-gold px-8 py-6 text-lg font-heading font-bold rounded-full"
          >
            View Our Menu
          </Button>
        </Link>
        <Link to="/book">
          <Button
            data-testid="hero-book-btn"
            variant="outline"
            className="px-8 py-6 text-lg font-heading font-bold rounded-full bg-white/20 text-white border-white hover:bg-white hover:text-seaside-magenta"
          >
            Book Seaside Bites
          </Button>
        </Link>
      </div>

      <div
        className="mt-12 flex flex-wrap justify-center gap-6 fade-in-up"
        style={{ animationDelay: "0.6s" }}
      >
        <a
          href="tel:727-833-9500"
          data-testid="hero-phone"
          className="flex items-center gap-2 text-white hover:text-seaside-gold transition-colors duration-300"
        >
          <Phone className="w-5 h-5" />
          <span className="font-body font-semibold">727-833-9500</span>
        </a>
        <div className="flex items-center gap-2 text-white">
          <MapPin className="w-5 h-5" />
          <span className="font-body font-semibold">Pasco County, Florida</span>
        </div>
      </div>
    </div>

    <div className="absolute bottom-0 left-0 right-0">
      <WaveDivider color="#FAF4E1" />
    </div>
  </section>
);

const AboutSection = () => (
  <section data-testid="about-section" className="bg-seaside-sand py-20 md:py-28">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div className="order-2 lg:order-1">
          <div className="relative">
            <div className="absolute -inset-4 bg-seaside-magenta/20 rounded-3xl rotate-3" />
            <img
              src={PIRATE_PHOTO_URL}
              alt="Seaside Bites Pirate Character"
              data-testid="about-image"
              className="relative rounded-3xl shadow-2xl w-full max-w-lg mx-auto"
            />
          </div>
        </div>

        <div className="order-1 lg:order-2 space-y-6">
          <div>
            <span className="font-accent text-3xl text-seaside-teal">
              A Little About Us
            </span>
            <h2 className="font-heading font-bold text-4xl md:text-5xl text-seaside-navy mt-2">
              Our Story
            </h2>
          </div>

          <div className="space-y-4 font-body text-lg text-gray-700 leading-relaxed">
            <p>
              <strong className="text-seaside-magenta">
                Seaside Bites on the Water
              </strong>{" "}
              is no longer just a dream, it's a reality.
            </p>
            <p>
              We're a pink pirate–inspired food boat bringing fun, flavor, and
              a little island magic to the local waters of Pasco County.
              Servicing the surrounding islands, we're here to make your beach
              days even better with fresh, made-to-order favorites.
            </p>
            <p>
              Our menu is all about easy, delicious eats—juicy hamburgers,
              classic hotdogs, cheesy quesadillas, flavorful tacos, refreshing
              virgin frozen drinks, and sweet ice cream treats to cool you off
              under the Florida sun.
            </p>
            <p>
              Whether you're anchored offshore, hanging out on the sandbar, or
              exploring the islands, Seaside Bites is your go-to spot on the
              water. We're more than just a food boat—we're part of your island
              experience.
            </p>
            <p className="font-semibold text-seaside-teal">
              Come find us, grab a bite, and enjoy the adventure.
            </p>
          </div>

          <Link to="/book">
            <Button
              data-testid="about-book-btn"
              className="btn-primary px-8 py-4 text-lg font-heading font-bold rounded-full mt-4"
            >
              Book Us For Your Event
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  </section>
);

const MenuPreviewSection = () => (
  <>
    <WaveDividerAlt flip color="#FFFFFF" />
    <section data-testid="menu-preview-section" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <span className="font-accent text-3xl text-seaside-teal">
          Check Out Our
        </span>
        <h2 className="font-heading font-bold text-4xl md:text-5xl text-seaside-navy mt-2 mb-6">
          Savory Options
        </h2>
        <p className="font-body text-lg text-gray-600 max-w-2xl mx-auto mb-12">
          Indulge in our mouthwatering delights, crafted to perfection. Dive into
          flavors that will leave you craving more!
        </p>

        <Link to="/menu">
          <Button
            data-testid="menu-preview-btn"
            className="btn-primary px-10 py-5 text-xl font-heading font-bold rounded-full"
          >
            View Full Menu
            <ChevronRight className="w-6 h-6 ml-2" />
          </Button>
        </Link>
      </div>
    </section>
  </>
);

const CTASection = () => (
  <section
    data-testid="cta-section"
    className="relative py-24 md:py-32"
    style={{
      background: "linear-gradient(135deg, #00B4D8 0%, #0096C7 50%, #E91E8C 100%)",
    }}
  >
    <div className="absolute inset-0 overflow-hidden">
      <div className="absolute top-10 right-20 opacity-10">
        <Anchor className="w-48 h-48 text-white" />
      </div>
    </div>

    <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <h2 className="font-heading font-bold text-4xl md:text-5xl text-white mb-6 text-shadow">
        Ready to Book Seaside Bites?
      </h2>
      <p className="font-body text-xl text-white/90 mb-10 max-w-2xl mx-auto">
        Whether it's a beach party, sandbar gathering, or island adventure,
        let us bring the food to you!
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Link to="/book">
          <Button
            data-testid="cta-book-btn"
            className="btn-gold px-10 py-5 text-xl font-heading font-bold rounded-full"
          >
            Book Us Now
          </Button>
        </Link>
        <a href="tel:727-833-9500">
          <Button
            data-testid="cta-call-btn"
            variant="outline"
            className="px-10 py-5 text-xl font-heading font-bold rounded-full bg-white/20 text-white border-white hover:bg-white hover:text-seaside-teal"
          >
            <Phone className="w-5 h-5 mr-2" />
            Call Us
          </Button>
        </a>
      </div>
    </div>
  </section>
);

export default function HomePage() {
  return (
    <div data-testid="home-page" className="overflow-hidden">
      <HeroSection />
      <AboutSection />
      <MenuPreviewSection />
      <CTASection />
    </div>
  );
}
